import { Option } from "@inrev/core";
import { latestEffectiveDecision, topOpenRequests } from "@portal/api/decisioning";
import { type ReactNode } from "react";
import { cn } from "../../../../lib/utils";
import { RetryableLoadError } from "../../../../patterns/retryable-load-error";
import { Tag, Text } from "../../../../primitives";
import {
	DATA_NEEDED_IMPORTANCE_LABELS,
	DATA_NEEDED_IMPORTANCE_TAG_COLORS,
	DECISION_STAGE_LABELS,
	DECISION_STAGE_TAG_COLORS,
	formatDecisioningDate,
} from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import { useDecisioningPanel } from "../DecisioningPanelContext";

const WAITING_ON_LIMIT = 3;

const rowButtonClass =
	"block w-full rounded-md text-left outline-none focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30 hover:bg-surface-light";

const OverviewRow = ({
	label,
	testId,
	children,
}: {
	label: string;
	testId: string;
	children: ReactNode;
}) => (
	<div
		className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 border-b border-bc-subtle py-4"
		data-testid={testId}
	>
		<Text type="eyebrow" color="tertiary" className="pt-[3px]">
			{label}
		</Text>
		<div className="min-w-0">{children}</div>
	</div>
);

const LastRow = () => {
	const { decisions, decisionsLoadError, userName } = useDecisioningData();
	const { jumpTo } = useDecisioningPanel();
	const last = latestEffectiveDecision(decisions);

	return (
		<OverviewRow label="Last" testId="overview-last">
			{Option.isSome(decisionsLoadError) ? (
				<RetryableLoadError
					resource="decisions"
					onRetry={decisionsLoadError.retry}
					testId="overview-last-load-error"
				/>
			) : Option.isNone(last) ? (
				<Text type="body" color="tertiary" data-testid="overview-last-empty">
					No decision logged yet.
				</Text>
			) : (
				<button
					type="button"
					className={cn(rowButtonClass, "flex flex-col items-start gap-2")}
					onClick={() => jumpTo({ tab: "history", id: last.id })}
					data-testid="overview-last-button"
				>
					<Tag color={DECISION_STAGE_TAG_COLORS[last.stage]}>
						{DECISION_STAGE_LABELS[last.stage]}
					</Tag>
					<span className="flex items-baseline gap-2">
						<Text type="caption" color="tertiary" className="tabular-nums">
							{formatDecisioningDate(last.createdAt)}
						</Text>
						<Text type="caption" color="tertiary">
							{userName(last.underwriterUserId)}
						</Text>
					</span>
					<Text
						type="body"
						color="secondary"
						className="line-clamp-2 block text-[13px] leading-[18px] [text-box-trim:none]"
					>
						{last.rationale}
					</Text>
				</button>
			)}
		</OverviewRow>
	);
};

const WaitingOnRow = () => {
	const { dataNeeded } = useDecisioningData();
	const { jumpTo, setTab } = useDecisioningPanel();
	const { top, remaining } = topOpenRequests(dataNeeded, WAITING_ON_LIMIT);

	return (
		<OverviewRow label="Waiting on" testId="overview-waiting">
			{top.length === 0 ? (
				<Text type="body" color="tertiary" data-testid="overview-waiting-empty">
					Nothing outstanding.
				</Text>
			) : (
				<div className="flex flex-col gap-3">
					{top.map((item) => (
						<button
							key={item.id}
							type="button"
							className={cn(rowButtonClass, "flex items-start gap-3")}
							onClick={() => jumpTo({ tab: "data-needed", id: item.id })}
							data-testid={`overview-waiting-${item.id}`}
						>
							<span className="flex min-w-0 flex-1 flex-col gap-1.5">
								<Text
									type="body"
									color="primary"
									className="line-clamp-2 block break-words text-[13px] font-medium leading-[18px] [text-box-trim:none]"
								>
									{item.title}
								</Text>
								{item.assignee !== "Agent" && (
									<Text type="caption" color="tertiary" className="block">
										{item.assignee}
									</Text>
								)}
							</span>
							<Tag className="shrink-0" color={DATA_NEEDED_IMPORTANCE_TAG_COLORS[item.importance]}>
								{DATA_NEEDED_IMPORTANCE_LABELS[item.importance]}
							</Tag>
						</button>
					))}
					{remaining > 0 && (
						<button
							type="button"
							className="w-fit rounded-md text-left outline-none focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30"
							onClick={() => setTab("data-needed")}
							data-testid="overview-waiting-more"
						>
							<Text type="caption" color="link" className="hover:underline">
								{`+${remaining} more`}
							</Text>
						</button>
					)}
				</div>
			)}
		</OverviewRow>
	);
};

const FactorTile = ({
	kind,
	label,
	count,
	dotClass,
}: {
	kind: "pro" | "con";
	label: string;
	count: number;
	dotClass: string;
}) => {
	const { jumpTo } = useDecisioningPanel();
	return (
		<button
			type="button"
			className="flex flex-col gap-1.5 rounded-lg border border-bc-default p-3.5 text-left outline-none hover:bg-surface-light focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30"
			onClick={() => jumpTo({ tab: "factors", id: `column:${kind}` })}
			data-testid={`overview-tile-${kind}`}
		>
			<span className="flex items-center gap-1.5">
				<span aria-hidden className={cn("size-1.5 rounded-full", dotClass)} />
				<Text type="caption" color="tertiary">
					{label}
				</Text>
			</span>
			<Text type="title" color="primary" className="text-[18px] leading-[24px] tabular-nums">
				{count}
			</Text>
		</button>
	);
};

const FactorsRow = () => {
	const { proCons } = useDecisioningData();
	return (
		<OverviewRow label="Factors" testId="overview-factors">
			<div className="grid grid-cols-2 gap-3">
				<FactorTile
					kind="pro"
					label="Pros"
					count={proCons.filter((item) => item.kind === "pro").length}
					dotClass="bg-confirm"
				/>
				<FactorTile
					kind="con"
					label="Cons"
					count={proCons.filter((item) => item.kind === "con").length}
					dotClass="bg-danger"
				/>
			</div>
		</OverviewRow>
	);
};

export const OverviewTab = () => (
	<div className="flex flex-col" data-testid="decisioning-overview">
		<LastRow />
		<WaitingOnRow />
		<FactorsRow />
	</div>
);
